import { configureStore, ThunkAction, Action } from '@reduxjs/toolkit'
import AuthenticationReducer from '@Features/Authentication/AuthenticationSlice/Authentication.slice'
import { setupListeners } from '@reduxjs/toolkit/dist/query'
import classroomApi from '@Features/Classroom/ClassroomSlice/ClassroomSlice'
import { ProgressAPI } from '@Features/Progress/ProgressSlice/ProgressSlice'
import settingsSlice from '@Features/Settings/SettingsSlice/Settings.slice'
import { BaseApi } from '@Features/Base/BaseApi/BaseApi'
import SnackbarSlice from './SnackbarSlice/SnackbarSlice'
import RunningTestSlice from './RunningTestSlice/RunningTestSlice'
import UtilsSlice from './UtilsSlice/UtilsSlice'
import { createStateSyncMiddleware, initMessageListener } from 'redux-state-sync'

// RTK Query actions are tab-private and their meta holds non-cloneable Request/Response
// objects, so broadcasting them over the state-sync BroadcastChannel throws DataCloneError
// `auth/bootstrap/` is excluded alongside the RTK Query prefixes. Its `pending`
// case sets authReady=false, and redux-state-sync re-dispatches received
// actions verbatim — so a second tab booting would flip THIS tab's authReady
// off, and RequireAuth (which returns null until it is true) would unmount the
// whole guarded subtree mid-lesson, destroying unsaved form and player state.
// Each tab resolves its own session; that answer must not be broadcast.
const rtkQueryActionPrefixes = [`${BaseApi.reducerPath}/`, `${ProgressAPI.reducerPath}/`, 'auth/bootstrap/']

export const store = configureStore({
	reducer: {
		auth: AuthenticationReducer,
		// No classroomApi entry. It is BaseApi.enhanceEndpoints().injectEndpoints(),
		// which returns the SAME api instance — so its reducerPath IS
		// BaseApi.reducerPath and this wrote the identical key twice. Harmless at
		// runtime (the duplicate resolved to the same reducer) but it read as two
		// separate roots, and it was one of the errors a working compiler had
		// never been able to report.
		[SnackbarSlice.name]: SnackbarSlice.reducer,
		[RunningTestSlice.name]: RunningTestSlice.reducer,
		[ProgressAPI.reducerPath]: ProgressAPI.reducer,
		[settingsSlice.name]: settingsSlice.reducer,
		[BaseApi.reducerPath]: BaseApi.reducer,
		[UtilsSlice.name]: UtilsSlice.reducer,
	},
	middleware: getDefaultMiddleware =>
		getDefaultMiddleware({ serializableCheck: false })
			.concat(BaseApi.middleware)
			.concat(ProgressAPI.middleware)
			.concat(
				createStateSyncMiddleware({
					predicate: action => !rtkQueryActionPrefixes.some(prefix => String(action.type).startsWith(prefix)),
				})
			),
	devTools: true,
})

setupListeners(store.dispatch)
initMessageListener(store)

export type AppDispatch = typeof store.dispatch
export type RootState = ReturnType<typeof store.getState>
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>
